// What recording comes down to, as pure functions (TX-US-0028): which container the browser can
// produce, how the elapsed time and the level read, and what to say when the microphone refuses.
// The hook that drives MediaRecorder is useRecorder.js; nothing here touches a device.

// A recording stops itself here. The API allows a minute more, for the clock.
export const MAX_RECORDING_MS = 15 * 60 * 1000;

// Ample for speech, and a tenth of what a phone would otherwise send over a site connection.
export const AUDIO_BITS_PER_SECOND = 32_000;

// In order of preference: Opus in WebM (Chrome, Android), AAC in MP4 (iOS Safari), Opus in Ogg
// (Firefox). They mirror RECORDING_EXTENSIONS in trex/hub/models.py; the API is the authority.
const CONTAINERS = [
  'audio/webm;codecs=opus',
  'audio/webm',
  'audio/mp4',
  'audio/ogg;codecs=opus',
  'audio/ogg',
];

// The first container the browser says it can record, or null when it can record none.
export const pickContainer = (isTypeSupported) =>
  CONTAINERS.find((type) => isTypeSupported(type)) ?? null;

// The type without its parameters: what the recording is uploaded and stored as.
export const baseType = (type) => type.split(';')[0].trim().toLowerCase();

export const canRecord = (scope) =>
  Boolean(scope.MediaRecorder && scope.navigator?.mediaDevices?.getUserMedia);

// m:ss, as a phone's own recorder shows it.
export function formatElapsed(ms) {
  const seconds = Math.max(0, Math.floor(ms / 1000));
  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;
}

// At least a second: a tap that starts and stops at once is still a recording of something.
export const durationSeconds = (ms) => Math.max(1, Math.round(ms / 1000));

// How loud the last frame was, 0 to 1, from an analyser's byte samples (silence is 128). Speech
// sits low on a linear scale, so it is lifted to make a quiet voice visibly move the ring.
export function levelOf(samples) {
  if (samples.length === 0) return 0;
  let sum = 0;
  for (const sample of samples) {
    const centred = (sample - 128) / 128;
    sum += centred * centred;
  }
  return Math.min(1, Math.sqrt(sum / samples.length) * 4);
}

// Why the microphone did not start, in words the BD can act on. Text and files stay usable.
export function refusalMessage(error) {
  if (error?.name === 'NotAllowedError' || error?.name === 'SecurityError') {
    return 'Microphone access is blocked. Allow it for T-REX in your browser settings, or type your update instead.';
  }
  if (error?.name === 'NotFoundError' || error?.name === 'OverconstrainedError') {
    return 'No microphone was found on this device. Type your update instead.';
  }
  return 'The microphone could not be started. Try again, or type your update instead.';
}
